import React, { useState, useRef, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import './ClusterNavItem.css'

const MOBILE_BREAKPOINT = 768

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
    )
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])
    return isMobile
}

/**
 * Renders one hub + its spokes as a single sidebar entry.
 * Desktop: the hub link navigates directly; a separate chevron toggles an
 * inline expandable list of spokes below it.
 * Mobile: the sidebar is a horizontal icon strip, so tapping the hub icon
 * instead opens a compact popover anchored above that icon — sized to its
 * content and clamped inside the viewport — listing the hub plus every spoke.
 */
const ClusterNavItem = ({ hub, spokes, icon: Icon }) => {
    const location = useLocation()
    const isMobile = useIsMobile()

    const allPaths = [hub.path, ...spokes.map(s => s.path)]
    const isOnCluster = allPaths.some(
        p => location.pathname === p || location.pathname === p.replace(/\/$/, '')
    )

    const [isOpen, setIsOpen] = useState(!isMobile && isOnCluster)
    const [popoverStyle, setPopoverStyle] = useState({})
    const triggerRef = useRef(null)
    const popoverRef = useRef(null)

    useEffect(() => {
        if (!isMobile && isOnCluster) setIsOpen(true)
    }, [isMobile, isOnCluster])

    // Close the mobile popover whenever the route changes.
    useEffect(() => {
        setIsOpen(false)
    }, [location.pathname])

    // Close on outside click, scroll, or Escape (mobile popover only).
    useEffect(() => {
        if (!isMobile || !isOpen) return

        const handleClick = (e) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                setIsOpen(false)
            }
        }
        const handleKey = (e) => {
            if (e.key === 'Escape') setIsOpen(false)
        }
        const handleScroll = () => setIsOpen(false)

        document.addEventListener('mousedown', handleClick)
        document.addEventListener('touchstart', handleClick)
        document.addEventListener('keydown', handleKey)
        document.addEventListener('scroll', handleScroll, { capture: true, passive: true })
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('touchstart', handleClick)
            document.removeEventListener('keydown', handleKey)
            document.removeEventListener('scroll', handleScroll, { capture: true })
        }
    }, [isMobile, isOpen])

    // Position the popover above the trigger icon, clamped to the viewport.
    useEffect(() => {
        if (!isMobile || !isOpen || !triggerRef.current) return

        const positionPopover = () => {
            const rect = triggerRef.current.getBoundingClientRect()
            const popoverWidth = popoverRef.current?.offsetWidth || 180
            const margin = 8
            let left = rect.left + rect.width / 2 - popoverWidth / 2
            left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin))

            setPopoverStyle({
                left: `${left}px`,
                bottom: `${window.innerHeight - rect.top + 10}px`,
            })
        }

        positionPopover()
        window.addEventListener('resize', positionPopover)
        return () => window.removeEventListener('resize', positionPopover)
    }, [isMobile, isOpen])

    const handleTriggerClick = (e) => {
        if (isMobile) {
            e.preventDefault()
            setIsOpen(prev => !prev)
        }
    }

    return (
        <li className="sidebar-nav-item cluster-nav-item">
            <div className="cluster-nav-header">
                <NavLink
                    ref={triggerRef}
                    to={hub.path}
                    onClick={handleTriggerClick}
                    aria-haspopup={isMobile ? 'true' : undefined}
                    aria-expanded={isMobile ? isOpen : undefined}
                    className={({ isActive }) =>
                        ['sidebar-nav-link', 'cluster-nav-link', (isMobile ? isOnCluster : isActive) && 'sidebar-nav-link-active']
                            .filter(Boolean).join(' ')
                    }
                >
                    <span className="sidebar-nav-icon"><Icon size={20} /></span>
                    <span className="sidebar-nav-text">{hub.label}</span>
                </NavLink>

                {!isMobile && (
                    <button
                        type="button"
                        className="cluster-nav-toggle"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? `Collapse ${hub.label} tools` : `Expand ${hub.label} tools`}
                    >
                        <ChevronDown size={16} className={`cluster-nav-chevron ${isOpen ? 'open' : ''}`} />
                    </button>
                )}
            </div>

            {!isMobile && (
                <ul className={`cluster-nav-items ${isOpen ? 'expanded' : ''}`} aria-hidden={!isOpen}>
                    {spokes.map((spoke) => (
                        <li key={spoke.path}>
                            <NavLink
                                to={spoke.path}
                                tabIndex={isOpen ? 0 : -1}
                                className={({ isActive }) => ['cluster-nav-subitem', isActive && 'active'].filter(Boolean).join(' ')}
                            >
                                {spoke.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            )}

            {isMobile && isOpen && (
                <ul className="cluster-nav-popover" style={popoverStyle} ref={popoverRef} role="menu">
                    <li className="cluster-nav-popover-arrow" aria-hidden="true" />
                    <li>
                        <NavLink to={hub.path} className="cluster-nav-popover-item cluster-nav-popover-hub" role="menuitem">
                            {hub.label}
                        </NavLink>
                    </li>
                    {spokes.map((spoke) => (
                        <li key={spoke.path}>
                            <NavLink to={spoke.path} className="cluster-nav-popover-item" role="menuitem">
                                {spoke.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    )
}

export default ClusterNavItem